import { getUserTimezone, sanitizeCommandInput } from '@/utils/timeUtils';

export type Skill = {
  /** Unique command identifier, used as the slash command (e.g., "analyze-campaigns" -> /analyze-campaigns) */
  shortName: string;
  /** Human-readable name shown in the command panel */
  displayName: string;
  /** Short description of what the skill does */
  description: string;
  /**
   * Optional skill-specific message composer.
   * Receives the sanitized user text and returns the final slash command string.
   * When not provided, the default `/<shortName> <text>` format is used.
   */
  composeMessage?: (text: string) => string;
};

/** Full skill detail including content and categories, used by the Skills listing page. */
export type SkillDetail = Omit<Skill, 'composeMessage'> & {
  content: string;
  categories: { shortName: string; displayName: string }[];
};

/** Shape of a single skill item returned by the API. */
export interface SkillApiItem {
  short_name: string;
  display_name: string;
  description: string;
  content: string;
  skill_categories: { short_name: string; display_name: string }[];
}

/** Shape of the GET /api/v1/skill response. */
export interface SkillsResponse {
  skills: SkillApiItem[];
}

/**
 * Skills that have v2 (tool-UI rendering) variants.
 * Used to swap between old and new skill versions based on the `tool_ui_rendering` feature flag.
 */
export const V2_SKILL_PAIRS: Record<string, string> = {
  blog_post_refresh: 'blog_post_refresh_v2',
  blog_post_strategy: 'blog_post_strategy_v2',
  create_experiment: 'create_experiment_v2',
  create_google_ads_campaign: 'create_google_ads_campaign_v2',
  generate_report: 'generate_report_v2',
  humanize_content: 'humanize_content_v2',
};

const V2_SHORT_NAMES = new Set(Object.values(V2_SKILL_PAIRS));
const OLD_SHORT_NAMES_WITH_V2 = new Set(Object.keys(V2_SKILL_PAIRS));

/** Filter skills based on tool_ui_rendering flag. */
export function filterSkillsByRenderingMode<T extends { shortName: string }>(
  skills: T[],
  toolUIRenderingEnabled: boolean,
): T[] {
  if (toolUIRenderingEnabled) {
    // Show v2 skills, hide originals that have v2 counterparts
    return skills.filter((s) => !OLD_SHORT_NAMES_WITH_V2.has(s.shortName));
  }
  // Hide v2 skills
  return skills.filter((s) => !V2_SHORT_NAMES.has(s.shortName));
}

/**
 * Client-side compose overrides for skills that need custom message formatting.
 * Keyed by skill shortName. Applied in useSkills after fetching from the API.
 */
export const SKILL_COMPOSE_OVERRIDES: Record<string, (text: string) => string> = {
  create_routine: (text: string) => {
    const sanitized = sanitizeCommandInput(text.trim());
    const base = sanitized
      ? `/create_routine ${sanitized}`
      : '/create_routine';
    return `${base} [timezone: ${getUserTimezone()}]`;
  },
};
